// src/shared/protocol/messages.ts
import type { GameSettings, GameEvent, GameState } from '@shared/game';
import type { MinigameFlowSnapshot, MinigameRewardGrant } from '@shared/minigames/registry';
import type {
  CharacterCustomization,
  DroppedKeyPile,
  EndGameSnapshot,
  PendingForkChoice,
  SoulAnchorMode,
} from '@shared/game/types';
import { ALL_CAPE_IDS, ALL_COLOR_IDS, ALL_EYE_IDS, ALL_HEAD_IDS } from '@shared/game/types';

export type ClientPresentationTraceStage =
  | 'modal_open'
  | 'modal_close'
  | 'dice_start'
  | 'dice_done'
  | 'dice_skipped'
  | 'movement_step_start'
  | 'movement_step_done'
  | 'state_sync_deferred'
  | 'state_sync_applied'
  | 'queue_defer'
  | 'queue_release'
  | 'turn_banner_requested'
  | 'turn_banner_shown'
  | 'turn_banner_done'
  | 'turn_banner_stale_dropped'
  | 'prompt_ready_pending'
  | 'prompt_ready_sent'
  | 'prompt_ready_stale_dropped'
  | 'relocation_reveal_queued'
  | 'relocation_reveal_blocked'
  | 'relocation_reveal_start'
  | 'relocation_reveal_done'
  | 'inventory_item_click'
  | 'notification_active_changed'
  | 'board_effect_active_changed';

export interface ClientPresentationTraceMessage {
  type: 'client_presentation_trace';
  stage: ClientPresentationTraceStage;
  playerId: string;
  round: number;
  currentPlayerIndex: number;
  screen?: string;
  phase?: GameState['phase'];
  activePlayerId?: string;
  turnKey?: string;
  nodeId?: string;
  itemId?: string;
  encounterKind?: 'treasure_chest' | 'weapons_cache';
  reason?: string;
  serverMessageType?: string;
  serverEventType?: string;
  eventTimestamp?: number;
  durationMs?: number;
  counts?: Record<string, number>;
}

export type ClientMessage =
  | {
      type: 'create_room';
      playerName: string;
      mode: 'board_game' | 'minigames_only';
      roomType: 'create_game' | 'quick_play';
      customization?: CharacterCustomization;
    }
  | {
      type: 'join_room';
      code: string;
      playerName: string;
      sessionToken?: string;
      customization?: CharacterCustomization;
    }
  | { type: 'reconnect'; sessionToken: string }
  | { type: 'restore_session'; token: string }
  | { type: 'leave' }
  | { type: 'configure'; settings: Partial<GameSettings> }
  | { type: 'add_bot'; difficulty: 'easy' | 'normal' | 'hard' }
  | { type: 'add_local_player'; playerName: string; localSlot: number }
  | { type: 'remove_player'; playerId: string }
  | { type: 'set_player_name'; playerName: string; playerId?: string }
  | { type: 'set_ready'; isReady: boolean; playerId?: string }
  | { type: 'start_game' }
  | { type: 'pause_game' }
  | { type: 'resume_game' }
  | {
      type: 'turn_intro_complete';
      playerId: string;
      round: number;
      currentPlayerIndex: number;
    }
  | {
      type: 'board_prompt_ready';
      promptKind: 'fork_choice' | 'soul_anchor_claim' | 'board_encounter';
      playerId: string;
      round: number;
      currentPlayerIndex: number;
      nodeId: string;
      encounterKind?: 'treasure_chest' | 'weapons_cache';
    }
  | ClientPresentationTraceMessage
  | { type: 'roll_dice'; nextNodeId?: string; playerId?: string }
  | { type: 'choose_fork'; nextNodeId: string; playerId?: string }
  | {
      type: 'use_item';
      itemId: string;
      playerId?: string;
      targetId?: string;
      targetNodeId?: string;
      direction?: { dx: number; dy: number };
    }
  | { type: 'skip_item'; playerId?: string }
  | { type: 'claim_soul_anchor'; choice?: SoulAnchorMode; playerId?: string }
  | { type: 'buy_trophy'; playerId?: string }
  | { type: 'skip_trophy'; playerId?: string }
  | { type: 'claim_weapons_cache'; itemId: string; playerId?: string }
  | { type: 'mystery_box_choice'; chosenMinigameId: string; playerId?: string }
  | { type: 'quick_play_request'; playerName: string; customization?: CharacterCustomization }
  | {
      type: 'subscribe_warmup_arena';
      preferredColor?: string;
      playerName?: string;
      customization?: CharacterCustomization;
      isReady?: boolean;
    }
  | { type: 'walk_to_arena_tile'; nodeId: string }
  | {
      type: 'start_solo_minigame';
      playerName?: string;
      customization?: CharacterCustomization;
    }
  | {
      type: 'minigame_input';
      minigameId: string;
      instanceId: string;
      inputPhaseId?: string;
      payload: unknown;
      localSlot?: number;
      inputSeq?: number;
      clientTick?: number;
      clientCommandMs?: number;
      estimatedServerNowMs?: number;
      clockOffsetMs?: number;
      rttEstimateMs?: number;
      renderDelayMs?: number;
    }
  | {
      type: 'minigame_ready';
      minigameId: string;
      instanceId: string;
      phaseId?: string;
    }
  | {
      type: 'minigame_selection_ready';
      instanceId: string;
      minigameId: string;
    }
  | {
      type: 'minigame_selection_complete';
      instanceId: string;
      minigameId: string;
    }
  | { type: 'admin_force_minigame_now'; minigameId?: string }
  | { type: 'admin_set_minigame_override'; minigameId: string }
  | { type: 'admin_complete_minigame_now' }
  | { type: 'admin_god_auth'; token: string }
  | { type: 'admin_maintenance_set'; enabled: boolean; estimateMinutes?: number }
  | { type: 'admin_runtime_status' }
  | { type: 'admin_god_move_steps'; delta: number; playerId?: string }
  | { type: 'admin_god_teleport'; nodeId: string; playerId?: string }
  | {
      type: 'admin_god_use_item';
      itemId: string;
      playerId?: string;
      target?:
        | { kind: 'player' | 'tile'; id: string }
        | { kind: 'direction'; dx: number; dy: number };
    }
  | {
      type: 'admin_god_set_resources';
      playerId?: string;
      hp?: number;
      keys?: number;
      gold?: number;
      inventory?: string[];
    }
  | { type: 'admin_god_add_item'; playerId?: string; itemId: string }
  | { type: 'admin_god_set_immunity'; playerId?: string; noDamage: boolean; noKeyLoss: boolean }
  | { type: 'admin_god_advance_turn' }
  | { type: 'admin_god_force_phase'; phase: GameState['phase'] }
  | { type: 'admin_god_pick_fork'; nodeId: string }
  | { type: 'emote'; playerId: string; emoteId: 1 | 2 | 3 | 4 | 5 | 6 }
  | {
      type: 'set_character_customization';
      customization: CharacterCustomization;
      playerId?: string;
    };

// NOTE on `event` variant: spec §6 enumerates 15 fine-grained server→client gameplay events
// (dice_result, player_move, fork_choice_required, tile_effect, item_used, item_targeting,
// player_damaged, player_died, keys_changed, trophy_bought, stalker_move, minigame_start,
// minigame_state, minigame_results, awards). All 15 are NOT separate ServerMessage variants —
// they are carried by the single generic `{ type: 'event'; event: GameEvent }` variant via
// the event-log bridge (Task 5.12). Clients discriminate on `event.type`.
// `player_left`, `player_disconnected`, `player_reconnected` ARE separate variants because
// they reflect connection-layer state, not in-game events.
export interface RoomScopedRevisionFields {
  roomId?: string;
  roomRevision?: number;
  checkpointRevision?: number;
  serverEpoch?: string;
}

export interface JoinedRoomPlayerPayload {
  id: string;
  name: string;
  isBot: boolean;
  isLocal: boolean;
  customization?: CharacterCustomization;
}

type ServerMessagePayload =
  | {
      type: 'room_created';
      roomId: string;
      code: string;
      sessionToken: string;
      hostId: string;
      ownedPlayerIds: string[];
    }
  | {
      type: 'room_joined';
      roomId: string;
      roomCode: string;
      playerId: string;
      sessionToken: string;
      hostId: string;
      mode: 'board_game' | 'minigames_only' | 'warmup-arena';
      roomType: 'create_game' | 'quick_play';
      players: JoinedRoomPlayerPayload[];
      ownedPlayerIds: string[];
    }
  | {
      type: 'player_joined';
      playerId: string;
      playerName: string;
      isBot: boolean;
      isLocal: boolean;
      sessionToken?: undefined;
      roomCode?: undefined;
    }
  | {
      type: 'session_restored';
      token: string;
      roomCode: string;
      playerId: string;
      playerName: string;
      hostId: string;
      roomType: 'create_game' | 'quick_play';
      mode: 'board_game' | 'minigames_only' | 'warmup-arena';
      phase: GameState['phase'];
      ownedPlayerIds: string[];
    }
  | {
      type: 'warmup_arena_joined';
      playerId: string;
      nodeId: string;
      roomId: string;
      sessionToken: string;
      roomCode: string;
      playerName?: string;
      isReady?: boolean;
      ownedPlayerIds: string[];
    }
  | {
      type: 'warmup_matchmaking_status';
      roomId: string;
      players: Array<{ id: string; name: string; color: CharacterCustomization['color'] }>;
      capacity: number;
      countdownMs: number | null;
      statusText: string;
    }
  | {
      type: 'arena_main_game_snapshot';
      players: Array<{
        playerId: string;
        nodeId: string;
        headId: CharacterCustomization['head'];
        color: number;
      }>;
    }
  | {
      type: 'quick_play_joined';
      roomId: string;
      sessionToken?: string;
      roomCode?: string;
      playerId?: string;
      players: Array<{ id: string; name: string }>;
      countdownMs: number;
      countdownDeadlineAt?: number;
      ownedPlayerIds: string[];
    }
  | { type: 'game_started'; seed: number }
  | { type: 'game_paused'; pausedByPlayerId: string; pausedAtMs: number }
  | { type: 'game_resumed' }
  | { type: 'phase_change'; phase: string }
  | {
      type: 'minigame_selection';
      instanceId: string;
      minigameId: string;
      displayName: string;
      seed: number;
      players: Array<{ id: string; displayName: string; customization?: CharacterCustomization }>;
      selectionPool: Array<{ id: string; displayName: string }>;
      selectedIndex: number;
      startedAtMs: number;
      timeoutAtMs: number;
      serverNowMs: number;
    }
  | {
      type: 'minigame_start';
      instanceId: string;
      minigameId: string;
      displayName: string;
      seed: number;
      players: Array<{ id: string; displayName: string; customization?: CharacterCustomization }>;
    }
  | {
      type: 'minigame_state';
      instanceId: string;
      minigameId: string;
      phase: string;
      roundIndex?: number;
    }
  | {
      type: 'minigame_snapshot';
      instanceId: string;
      minigameId: string;
      serverNowMs: number;
      phase: string;
      roundIndex?: number;
      sequence: number;
      serverTick?: number;
      phaseId?: string;
      flow?: MinigameFlowSnapshot;
      lastProcessedInputSeq?: Record<string, number>;
      payload: unknown;
    }
  | {
      type: 'minigame_event';
      instanceId: string;
      minigameId: string;
      serverNowMs: number;
      sequence: number;
      roundIndex?: number;
      phaseId?: string;
      event: unknown;
    }
  | {
      type: 'minigame_ready_state';
      instanceId: string;
      minigameId: string;
      phaseId?: string;
      readyPlayerIds: string[];
      requiredPlayerIds: string[];
      serverNowMs: number;
      playStartsAtMs?: number;
    }
  | {
      type: 'minigame_round_results';
      instanceId: string;
      minigameId: string;
      roundIndex: number;
      phaseId?: string;
      placements: Array<{ playerId: string; rank: number; count: number; score: number }>;
    }
  | {
      type: 'minigame_results';
      instanceId: string;
      minigameId: string;
      placements: Array<{ playerId: string; rank: number; score: number }>;
      rewards: MinigameRewardGrant[];
    }
  | { type: 'state_sync'; state: SerializedGameState; godMode?: StateSyncGodModeDebug }
  | { type: 'event'; event: GameEvent }
  | { type: 'fork_options'; playerId: string; atNodeId: string; options: string[] }
  | { type: 'settings_changed'; settings: GameSettings }
  | { type: 'owned_players_changed'; ownedPlayerIds: string[] }
  | { type: 'emote_played'; playerId: string; emoteId: 1 | 2 | 3 | 4 | 5 | 6; at: number }
  | { type: 'leave_ack' }
  | { type: 'player_left'; playerId: string }
  | { type: 'player_disconnected'; playerId: string }
  | { type: 'player_reconnected'; playerId: string }
  | { type: 'host_changed'; oldHostId: string; newHostId: string | null }
  | { type: 'game_over'; winnerId: string | null; endGame: EndGameSnapshot }
  | {
      type: 'admin_runtime_status';
      maintenance: { enabled: boolean; startedAt: number | null; estimateMinutes: number };
      activeGameRooms: number;
      connections: number;
      rooms: number;
      lobbyRooms: number;
      quickPlayRooms: number;
      warmupRooms: number;
      players: number;
      humanPlayers: number;
      botPlayers: number;
      roomsWithActiveMinigame: number;
      safeToDeploy: boolean;
      blockers: string[];
    }
  | {
      type: 'notification';
      level: 'info' | 'warn' | 'error';
      text: string;
      activeGameRooms?: number;
      estimateMinutes?: number;
    };

export type ServerMessage = ServerMessagePayload & RoomScopedRevisionFields;

export interface Envelope<T> {
  seq: number;
  payload: T;
}

export interface SerializedGameState {
  phase: GameState['phase'];
  round: number;
  maxRounds: number;
  currentPlayerIndex: number;
  turnOrder: string[];
  players: Array<[string, GameState['players'] extends Map<string, infer V> ? V : never]>;
  boardNodeIds: string[];
  treasureChestNodeId: string;
  settings: GameState['settings'];
  eventLog: GameState['eventLog'];
  rngSeed: number;
  rngState: number;
  weaponsCacheNodeId: string | null;
  weaponsCacheRespawnIn: number;
  stats: Array<[string, GameState['stats'] extends Map<string, infer V> ? V : never]>;
  boardModifiers: GameState['boardModifiers'];
  droppedKeys: Array<[string, DroppedKeyPile[] | number]>;
  lastMoveByPlayer: Array<
    [string, GameState['lastMoveByPlayer'] extends Map<string, infer V> ? V : never]
  >;
  activeClouds: GameState['activeClouds'];
  npcs: GameState['npcs'];
  activeCursedRelics: GameState['activeCursedRelics'];
  pendingOrbitalCannon: GameState['pendingOrbitalCannon'];
  mysteryBoxTraps: GameState['mysteryBoxTraps'];
  pendingMysteryBoxChoice: GameState['pendingMysteryBoxChoice'];
  pendingSoulDuel: GameState['pendingSoulDuel'];
  soulAnchorClaimModes?: Array<[string, SoulAnchorMode]>;
  pendingSoulAnchorClaim?: GameState['pendingSoulAnchorClaim'];
  pendingBoardEncounter?: GameState['pendingBoardEncounter'];
  pendingRelocationPrompts?: GameState['pendingRelocationPrompts'];
  pendingForkChoice?: PendingForkChoice | null;
  endGame?: EndGameSnapshot | null;
  awardsApplied?: boolean;
  turnActionDeadlineAtMs?: number | null;
  forkChoiceDeadlineAtMs?: number | null;
  pause?: GameState['pause'];
}

export interface StateSyncGodModeDebug {
  diceValue: number | null;
  pendingMoveSteps: number | null;
  pendingFork: { playerId: string; atNodeId: string; options: string[] } | null;
  immunity: Array<[string, { noDamage: boolean; noKeyLoss: boolean }]>;
  lastMove: {
    source: 'roll_dice' | 'admin_god_move_steps' | 'admin_god_teleport';
    playerId: string;
    requestedSteps: number | null;
    diceValue: number | null;
    beforeNodeId: string;
    afterNodeId: string;
    path: string[];
    executedMoves: Array<{ stepIndex: number; fromNodeId: string; toNodeId: string }>;
    stepsUsed: number;
    forksEncountered: number;
  } | null;
}

export type ParseResult = { ok: true; msg: ClientMessage } | { ok: false; error: string };

const GAME_PHASES = new Set<GameState['phase']>([
  'lobby',
  'game_start',
  'item_phase',
  'rolling',
  'moving',
  'fork_choice',
  'landing_effect',
  'chest_phase',
  'npc_phase',
  'minigame_announce',
  'minigame_countdown',
  'minigame_play',
  'minigame_results',
  'awards',
  'game_over',
]);

const CLIENT_PRESENTATION_TRACE_STAGES = new Set<ClientPresentationTraceStage>([
  'modal_open',
  'modal_close',
  'dice_start',
  'dice_done',
  'dice_skipped',
  'movement_step_start',
  'movement_step_done',
  'state_sync_deferred',
  'state_sync_applied',
  'queue_defer',
  'queue_release',
  'turn_banner_requested',
  'turn_banner_shown',
  'turn_banner_done',
  'turn_banner_stale_dropped',
  'prompt_ready_pending',
  'prompt_ready_sent',
  'prompt_ready_stale_dropped',
  'relocation_reveal_queued',
  'relocation_reveal_blocked',
  'relocation_reveal_start',
  'relocation_reveal_done',
  'inventory_item_click',
  'notification_active_changed',
  'board_effect_active_changed',
]);

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function isClientPresentationTraceStage(value: unknown): value is ClientPresentationTraceStage {
  return (
    typeof value === 'string' &&
    CLIENT_PRESENTATION_TRACE_STAGES.has(value as ClientPresentationTraceStage)
  );
}

const VALID_COLORS = new Set<string>(ALL_COLOR_IDS);
const VALID_HEADS = new Set<string>(ALL_HEAD_IDS);
const VALID_EYES = new Set<string>(ALL_EYE_IDS);
const VALID_CAPES = new Set<string>(ALL_CAPE_IDS);

function parseCharacterCustomization(
  value: unknown,
): { ok: true; customization: CharacterCustomization } | { ok: false; error: string } {
  if (typeof value !== 'object' || value === null)
    return { ok: false, error: 'invalid customization' };
  const cust = value as Record<string, unknown>;
  if (typeof cust.color !== 'string' || !VALID_COLORS.has(cust.color)) {
    return { ok: false, error: 'invalid customization.color' };
  }
  if (typeof cust.head !== 'string' || !VALID_HEADS.has(cust.head)) {
    return { ok: false, error: 'invalid customization.head' };
  }
  if (typeof cust.eyes !== 'string' || !VALID_EYES.has(cust.eyes)) {
    return { ok: false, error: 'invalid customization.eyes' };
  }
  if (typeof cust.cape !== 'string' || !VALID_CAPES.has(cust.cape)) {
    return { ok: false, error: 'invalid customization.cape' };
  }
  return {
    ok: true,
    customization: {
      color: cust.color as CharacterCustomization['color'],
      head: cust.head as CharacterCustomization['head'],
      eyes: cust.eyes as CharacterCustomization['eyes'],
      cape: cust.cape as CharacterCustomization['cape'],
    },
  };
}

function parseOptionalCharacterCustomization(
  value: unknown,
): { ok: true; customization?: CharacterCustomization } | { ok: false; error: string } {
  if (value === undefined) return { ok: true };
  const parsed = parseCharacterCustomization(value);
  if (!parsed.ok) return parsed;
  return { ok: true, customization: parsed.customization };
}

function parseGodModeTarget(value: unknown):
  | {
      ok: true;
      target?:
        | { kind: 'player' | 'tile'; id: string }
        | { kind: 'direction'; dx: number; dy: number };
    }
  | { ok: false; error: string } {
  if (value === undefined) return { ok: true };
  if (typeof value !== 'object' || value === null) return { ok: false, error: 'target invalid' };
  const target = value as Record<string, unknown>;
  if (target.kind !== 'player' && target.kind !== 'tile' && target.kind !== 'direction') {
    return { ok: false, error: 'target.kind required' };
  }
  if (target.kind === 'direction') {
    if (!isFiniteNumber(target.dx)) return { ok: false, error: 'target.dx required' };
    if (!isFiniteNumber(target.dy)) return { ok: false, error: 'target.dy required' };
    return { ok: true, target: { kind: 'direction', dx: target.dx, dy: target.dy } };
  }
  if (typeof target.id !== 'string') return { ok: false, error: 'target.id required' };
  return { ok: true, target: { kind: target.kind, id: target.id } };
}

function parseOptionalPlayerId(value: unknown): string | undefined | { error: string } {
  if (value === undefined) return undefined;
  if (typeof value !== 'string') return { error: 'playerId invalid' };
  return value;
}

function parseOptionalNumberRecord(
  value: unknown,
): { ok: true; value?: Record<string, number> } | { ok: false; error: string } {
  if (value === undefined) return { ok: true };
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return { ok: false, error: 'counts invalid' };
  }
  const parsed: Record<string, number> = {};
  for (const [key, item] of Object.entries(value)) {
    if (!isFiniteNumber(item)) return { ok: false, error: 'counts invalid' };
    parsed[key] = item;
  }
  return { ok: true, value: parsed };
}

export function parseClientMessage(raw: string): ParseResult {
  let obj: unknown;
  try {
    obj = JSON.parse(raw);
  } catch {
    return { ok: false, error: 'invalid json' };
  }

  if (
    typeof obj !== 'object' ||
    obj === null ||
    typeof (obj as Record<string, unknown>).type !== 'string'
  ) {
    return { ok: false, error: 'missing type' };
  }

  const o = obj as Record<string, unknown>;
  const type = o.type as string;

  switch (type) {
    case 'create_room': {
      if (typeof o.playerName !== 'string') return { ok: false, error: 'playerName required' };
      if (o.mode !== 'board_game' && o.mode !== 'minigames_only')
        return { ok: false, error: 'mode required' };
      if (o.roomType !== 'create_game' && o.roomType !== 'quick_play')
        return { ok: false, error: 'roomType required' };
      const customization = parseOptionalCharacterCustomization(o.customization);
      if (!customization.ok) return customization;
      return {
        ok: true,
        msg: {
          type,
          playerName: o.playerName,
          mode: o.mode,
          roomType: o.roomType,
          ...(customization.customization === undefined
            ? {}
            : { customization: customization.customization }),
        },
      };
    }
    case 'join_room': {
      if (typeof o.code !== 'string') return { ok: false, error: 'code required' };
      if (typeof o.playerName !== 'string') return { ok: false, error: 'playerName required' };
      const customization = parseOptionalCharacterCustomization(o.customization);
      if (!customization.ok) return customization;
      const msg: ClientMessage = { type, code: o.code, playerName: o.playerName };
      const withCustomization =
        customization.customization === undefined
          ? msg
          : { ...msg, customization: customization.customization };
      if (typeof o.sessionToken === 'string') {
        return { ok: true, msg: { ...withCustomization, sessionToken: o.sessionToken } };
      }
      return { ok: true, msg: withCustomization };
    }
    case 'reconnect': {
      if (typeof o.sessionToken !== 'string') return { ok: false, error: 'sessionToken required' };
      return { ok: true, msg: { type, sessionToken: o.sessionToken } };
    }
    case 'restore_session': {
      if (typeof o.token !== 'string') return { ok: false, error: 'token required' };
      return { ok: true, msg: { type, token: o.token } };
    }
    case 'leave':
      return { ok: true, msg: { type } };
    case 'configure': {
      if (typeof o.settings !== 'object' || o.settings === null)
        return { ok: false, error: 'settings required' };
      return { ok: true, msg: { type, settings: o.settings as Partial<GameSettings> } };
    }
    case 'add_bot': {
      if (o.difficulty !== 'easy' && o.difficulty !== 'normal' && o.difficulty !== 'hard') {
        return { ok: false, error: 'difficulty required' };
      }
      return { ok: true, msg: { type, difficulty: o.difficulty } };
    }
    case 'add_local_player': {
      if (typeof o.playerName !== 'string') return { ok: false, error: 'playerName required' };
      if (typeof o.localSlot !== 'number') return { ok: false, error: 'localSlot required' };
      return { ok: true, msg: { type, playerName: o.playerName, localSlot: o.localSlot } };
    }
    case 'remove_player': {
      if (typeof o.playerId !== 'string') return { ok: false, error: 'playerId required' };
      return { ok: true, msg: { type, playerId: o.playerId } };
    }
    case 'set_player_name': {
      if (typeof o.playerName !== 'string') return { ok: false, error: 'playerName required' };
      const playerId = parseOptionalPlayerId(o.playerId);
      if (typeof playerId === 'object') return { ok: false, error: playerId.error };
      return {
        ok: true,
        msg:
          playerId === undefined
            ? { type, playerName: o.playerName }
            : { type, playerName: o.playerName, playerId },
      };
    }
    case 'set_ready': {
      if (typeof o.isReady !== 'boolean') return { ok: false, error: 'isReady required' };
      if (typeof o.playerId === 'string') {
        return { ok: true, msg: { type, isReady: o.isReady, playerId: o.playerId } };
      }
      return { ok: true, msg: { type, isReady: o.isReady } };
    }
    case 'start_game':
      return { ok: true, msg: { type } };
    case 'pause_game':
      return { ok: true, msg: { type } };
    case 'resume_game':
      return { ok: true, msg: { type } };
    case 'turn_intro_complete': {
      if (typeof o.playerId !== 'string') return { ok: false, error: 'playerId required' };
      if (!Number.isInteger(o.round)) return { ok: false, error: 'round invalid' };
      if (!Number.isInteger(o.currentPlayerIndex)) {
        return { ok: false, error: 'currentPlayerIndex invalid' };
      }
      return {
        ok: true,
        msg: {
          type,
          playerId: o.playerId,
          round: o.round as number,
          currentPlayerIndex: o.currentPlayerIndex as number,
        },
      };
    }
    case 'board_prompt_ready': {
      if (
        o.promptKind !== 'fork_choice' &&
        o.promptKind !== 'soul_anchor_claim' &&
        o.promptKind !== 'board_encounter'
      ) {
        return { ok: false, error: 'promptKind invalid' };
      }
      if (typeof o.playerId !== 'string') return { ok: false, error: 'playerId required' };
      if (!Number.isInteger(o.round)) return { ok: false, error: 'round invalid' };
      if (!Number.isInteger(o.currentPlayerIndex)) {
        return { ok: false, error: 'currentPlayerIndex invalid' };
      }
      if (typeof o.nodeId !== 'string') return { ok: false, error: 'nodeId required' };
      if (
        o.encounterKind !== undefined &&
        o.encounterKind !== 'treasure_chest' &&
        o.encounterKind !== 'weapons_cache'
      ) {
        return { ok: false, error: 'encounterKind invalid' };
      }
      return {
        ok: true,
        msg: {
          type,
          promptKind: o.promptKind,
          playerId: o.playerId,
          round: o.round as number,
          currentPlayerIndex: o.currentPlayerIndex as number,
          nodeId: o.nodeId,
          ...(o.encounterKind === undefined ? {} : { encounterKind: o.encounterKind }),
        },
      };
    }
    case 'client_presentation_trace': {
      if (!isClientPresentationTraceStage(o.stage)) return { ok: false, error: 'stage invalid' };
      if (typeof o.playerId !== 'string') return { ok: false, error: 'playerId required' };
      if (!Number.isInteger(o.round)) return { ok: false, error: 'round invalid' };
      if (!Number.isInteger(o.currentPlayerIndex)) {
        return { ok: false, error: 'currentPlayerIndex invalid' };
      }
      if (
        o.encounterKind !== undefined &&
        o.encounterKind !== 'treasure_chest' &&
        o.encounterKind !== 'weapons_cache'
      ) {
        return { ok: false, error: 'encounterKind invalid' };
      }
      if (
        o.phase !== undefined &&
        (typeof o.phase !== 'string' || !GAME_PHASES.has(o.phase as GameState['phase']))
      ) {
        return { ok: false, error: 'phase invalid' };
      }

      const msg: ClientPresentationTraceMessage = {
        type,
        stage: o.stage,
        playerId: o.playerId,
        round: o.round as number,
        currentPlayerIndex: o.currentPlayerIndex as number,
      };
      if (typeof o.screen === 'string') msg.screen = o.screen;
      if (typeof o.phase === 'string') msg.phase = o.phase as GameState['phase'];
      if (typeof o.activePlayerId === 'string') msg.activePlayerId = o.activePlayerId;
      if (typeof o.turnKey === 'string') msg.turnKey = o.turnKey;
      if (typeof o.nodeId === 'string') msg.nodeId = o.nodeId;
      if (typeof o.itemId === 'string') msg.itemId = o.itemId;
      if (typeof o.reason === 'string') msg.reason = o.reason;
      if (typeof o.serverMessageType === 'string') msg.serverMessageType = o.serverMessageType;
      if (typeof o.serverEventType === 'string') msg.serverEventType = o.serverEventType;
      if (o.encounterKind !== undefined) msg.encounterKind = o.encounterKind;
      if (typeof o.eventTimestamp === 'number' && Number.isFinite(o.eventTimestamp)) {
        msg.eventTimestamp = o.eventTimestamp;
      }
      if (typeof o.durationMs === 'number' && Number.isFinite(o.durationMs)) {
        msg.durationMs = o.durationMs;
      }
      const counts = parseOptionalNumberRecord(o.counts);
      if (!counts.ok) return counts;
      if (counts.value !== undefined) msg.counts = counts.value;
      return { ok: true, msg };
    }
    case 'roll_dice': {
      const playerId = parseOptionalPlayerId(o.playerId);
      if (typeof playerId === 'object') return { ok: false, error: playerId.error };
      const msg: Extract<ClientMessage, { type: 'roll_dice' }> =
        playerId === undefined ? { type } : { type, playerId };
      if (typeof o.nextNodeId === 'string') {
        return { ok: true, msg: { ...msg, nextNodeId: o.nextNodeId } };
      }
      if ('nextNodeId' in o && o.nextNodeId !== undefined) {
        return { ok: false, error: 'nextNodeId must be string' };
      }
      return { ok: true, msg };
    }
    case 'choose_fork': {
      if (typeof o.nextNodeId !== 'string') return { ok: false, error: 'nextNodeId required' };
      const playerId = parseOptionalPlayerId(o.playerId);
      if (typeof playerId === 'object') return { ok: false, error: playerId.error };
      return {
        ok: true,
        msg:
          playerId === undefined
            ? { type, nextNodeId: o.nextNodeId }
            : { type, nextNodeId: o.nextNodeId, playerId },
      };
    }
    case 'use_item': {
      if (typeof o.itemId !== 'string') return { ok: false, error: 'itemId required' };
      const playerId = parseOptionalPlayerId(o.playerId);
      if (typeof playerId === 'object') return { ok: false, error: playerId.error };
      const msg: ClientMessage = { type, itemId: o.itemId };
      const withOptionals = { ...msg } as {
        type: 'use_item';
        itemId: string;
        playerId?: string;
        targetId?: string;
        targetNodeId?: string;
        direction?: { dx: number; dy: number };
      };
      if (playerId !== undefined) withOptionals.playerId = playerId;
      if (typeof o.targetId === 'string') withOptionals.targetId = o.targetId;
      if (typeof o.targetNodeId === 'string') withOptionals.targetNodeId = o.targetNodeId;
      if (typeof o.direction === 'object' && o.direction !== null) {
        const { dx, dy } = o.direction as Record<string, unknown>;
        if (typeof dx !== 'number' || !Number.isFinite(dx)) {
          return { ok: false, error: 'direction.dx invalid' };
        }
        if (typeof dy !== 'number' || !Number.isFinite(dy)) {
          return { ok: false, error: 'direction.dy invalid' };
        }
        withOptionals.direction = { dx, dy };
      }
      return { ok: true, msg: withOptionals };
    }
    case 'skip_item': {
      const playerId = parseOptionalPlayerId(o.playerId);
      if (typeof playerId === 'object') return { ok: false, error: playerId.error };
      return {
        ok: true,
        msg: playerId === undefined ? { type } : { type, playerId },
      };
    }
    case 'claim_soul_anchor': {
      if (o.choice !== 'keys' && o.choice !== 'hp') {
        return { ok: false, error: 'choice invalid' };
      }
      const playerId = parseOptionalPlayerId(o.playerId);
      if (typeof playerId === 'object') return { ok: false, error: playerId.error };
      return {
        ok: true,
        msg:
          playerId === undefined
            ? { type, choice: o.choice }
            : { type, choice: o.choice, playerId },
      };
    }
    case 'buy_trophy':
    case 'skip_trophy': {
      const playerId = parseOptionalPlayerId(o.playerId);
      if (typeof playerId === 'object') return { ok: false, error: playerId.error };
      return {
        ok: true,
        msg: playerId === undefined ? { type } : { type, playerId },
      };
    }
    case 'claim_weapons_cache': {
      if (typeof o.itemId !== 'string') return { ok: false, error: 'itemId required' };
      const playerId = parseOptionalPlayerId(o.playerId);
      if (typeof playerId === 'object') return { ok: false, error: playerId.error };
      return {
        ok: true,
        msg:
          playerId === undefined
            ? { type, itemId: o.itemId }
            : { type, itemId: o.itemId, playerId },
      };
    }
    case 'mystery_box_choice': {
      if (typeof o.chosenMinigameId !== 'string')
        return { ok: false, error: 'chosenMinigameId required' };
      const playerId = parseOptionalPlayerId(o.playerId);
      if (typeof playerId === 'object') return { ok: false, error: playerId.error };
      return {
        ok: true,
        msg:
          playerId === undefined
            ? { type, chosenMinigameId: o.chosenMinigameId }
            : { type, chosenMinigameId: o.chosenMinigameId, playerId },
      };
    }
    case 'quick_play_request': {
      if (typeof o.playerName !== 'string') return { ok: false, error: 'playerName required' };
      const customization = parseOptionalCharacterCustomization(o.customization);
      if (!customization.ok) return customization;
      return {
        ok: true,
        msg: {
          type,
          playerName: o.playerName,
          ...(customization.customization === undefined
            ? {}
            : { customization: customization.customization }),
        },
      };
    }
    case 'subscribe_warmup_arena': {
      if (o.preferredColor !== undefined && typeof o.preferredColor !== 'string') {
        return { ok: false, error: 'preferredColor required' };
      }
      if (o.playerName !== undefined && typeof o.playerName !== 'string') {
        return { ok: false, error: 'playerName required' };
      }
      if (o.isReady !== undefined && typeof o.isReady !== 'boolean') {
        return { ok: false, error: 'isReady required' };
      }
      const customization = parseOptionalCharacterCustomization(o.customization);
      if (!customization.ok) return customization;
      return {
        ok: true,
        msg: {
          type,
          ...(typeof o.preferredColor === 'string' ? { preferredColor: o.preferredColor } : {}),
          ...(typeof o.playerName === 'string' ? { playerName: o.playerName } : {}),
          ...(customization.customization === undefined
            ? {}
            : { customization: customization.customization }),
          ...(typeof o.isReady === 'boolean' ? { isReady: o.isReady } : {}),
        },
      };
    }
    case 'walk_to_arena_tile': {
      if (typeof o.nodeId !== 'string') return { ok: false, error: 'nodeId required' };
      return { ok: true, msg: { type, nodeId: o.nodeId } };
    }
    case 'start_solo_minigame': {
      if (o.playerName !== undefined && typeof o.playerName !== 'string') {
        return { ok: false, error: 'playerName required' };
      }
      const customization = parseOptionalCharacterCustomization(o.customization);
      if (!customization.ok) return customization;
      return {
        ok: true,
        msg: {
          type,
          ...(typeof o.playerName === 'string' ? { playerName: o.playerName } : {}),
          ...(customization.customization === undefined
            ? {}
            : { customization: customization.customization }),
        },
      };
    }
    case 'minigame_input': {
      if (typeof o.minigameId !== 'string') return { ok: false, error: 'minigameId required' };
      if (typeof o.instanceId !== 'string') return { ok: false, error: 'instanceId required' };
      const msg: Extract<ClientMessage, { type: 'minigame_input' }> = {
        type,
        minigameId: o.minigameId,
        instanceId: o.instanceId,
        payload: o.payload,
      };
      if (o.inputPhaseId !== undefined) {
        if (typeof o.inputPhaseId !== 'string' || o.inputPhaseId.length === 0) {
          return { ok: false, error: 'inputPhaseId invalid' };
        }
        msg.inputPhaseId = o.inputPhaseId;
      }
      if (typeof o.localSlot === 'number') msg.localSlot = o.localSlot;
      if (o.inputSeq !== undefined) {
        if (!Number.isInteger(o.inputSeq) || (o.inputSeq as number) < 0) {
          return { ok: false, error: 'inputSeq invalid' };
        }
        msg.inputSeq = o.inputSeq as number;
      }
      if (o.clientTick !== undefined) {
        if (!Number.isInteger(o.clientTick) || (o.clientTick as number) < 0) {
          return { ok: false, error: 'clientTick invalid' };
        }
        msg.clientTick = o.clientTick as number;
      }
      if (o.clientCommandMs !== undefined) {
        if (typeof o.clientCommandMs !== 'number' || !Number.isFinite(o.clientCommandMs)) {
          return { ok: false, error: 'clientCommandMs invalid' };
        }
        msg.clientCommandMs = o.clientCommandMs;
      }
      if (o.estimatedServerNowMs !== undefined) {
        if (
          typeof o.estimatedServerNowMs !== 'number' ||
          !Number.isFinite(o.estimatedServerNowMs)
        ) {
          return { ok: false, error: 'estimatedServerNowMs invalid' };
        }
        msg.estimatedServerNowMs = o.estimatedServerNowMs;
      }
      if (o.clockOffsetMs !== undefined) {
        if (typeof o.clockOffsetMs !== 'number' || !Number.isFinite(o.clockOffsetMs)) {
          return { ok: false, error: 'clockOffsetMs invalid' };
        }
        msg.clockOffsetMs = o.clockOffsetMs;
      }
      if (o.rttEstimateMs !== undefined) {
        if (
          typeof o.rttEstimateMs !== 'number' ||
          !Number.isFinite(o.rttEstimateMs) ||
          o.rttEstimateMs < 0
        ) {
          return { ok: false, error: 'rttEstimateMs invalid' };
        }
        msg.rttEstimateMs = o.rttEstimateMs;
      }
      if (o.renderDelayMs !== undefined) {
        if (
          typeof o.renderDelayMs !== 'number' ||
          !Number.isFinite(o.renderDelayMs) ||
          o.renderDelayMs < 0
        ) {
          return { ok: false, error: 'renderDelayMs invalid' };
        }
        msg.renderDelayMs = o.renderDelayMs;
      }
      return { ok: true, msg };
    }
    case 'minigame_ready': {
      if (typeof o.minigameId !== 'string') return { ok: false, error: 'minigameId required' };
      if (typeof o.instanceId !== 'string') return { ok: false, error: 'instanceId required' };
      if (o.phaseId !== undefined && (typeof o.phaseId !== 'string' || o.phaseId.length === 0)) {
        return { ok: false, error: 'phaseId invalid' };
      }
      return {
        ok: true,
        msg: {
          type,
          minigameId: o.minigameId,
          instanceId: o.instanceId,
          ...(o.phaseId === undefined ? {} : { phaseId: o.phaseId }),
        },
      };
    }
    case 'minigame_selection_ready':
    case 'minigame_selection_complete': {
      if (typeof o.minigameId !== 'string') return { ok: false, error: 'minigameId required' };
      if (typeof o.instanceId !== 'string') return { ok: false, error: 'instanceId required' };
      return {
        ok: true,
        msg: { type, minigameId: o.minigameId, instanceId: o.instanceId },
      };
    }
    case 'admin_force_minigame_now': {
      const msg: ClientMessage = { type };
      if (typeof o.minigameId === 'string') {
        return { ok: true, msg: { ...msg, minigameId: o.minigameId } };
      }
      return { ok: true, msg };
    }
    case 'admin_set_minigame_override': {
      if (typeof o.minigameId !== 'string' || o.minigameId.trim().length === 0) {
        return { ok: false, error: 'minigameId required' };
      }
      return { ok: true, msg: { type, minigameId: o.minigameId } };
    }
    case 'admin_complete_minigame_now':
      return { ok: true, msg: { type } };
    case 'admin_god_auth': {
      if (typeof o.token !== 'string' || o.token.trim().length === 0) {
        return { ok: false, error: 'token required' };
      }
      return { ok: true, msg: { type, token: o.token } };
    }
    case 'admin_maintenance_set': {
      if (typeof o.enabled !== 'boolean') return { ok: false, error: 'enabled required' };
      if (
        o.estimateMinutes !== undefined &&
        (!isFiniteNumber(o.estimateMinutes) || o.estimateMinutes < 1)
      ) {
        return { ok: false, error: 'estimateMinutes invalid' };
      }
      return {
        ok: true,
        msg: {
          type,
          enabled: o.enabled,
          ...(typeof o.estimateMinutes === 'number' ? { estimateMinutes: o.estimateMinutes } : {}),
        },
      };
    }
    case 'admin_runtime_status':
      return { ok: true, msg: { type } };
    case 'admin_god_move_steps': {
      if (!isFiniteNumber(o.delta) || !Number.isInteger(o.delta)) {
        return { ok: false, error: 'delta required' };
      }
      const playerId = parseOptionalPlayerId(o.playerId);
      if (typeof playerId === 'object') return { ok: false, error: playerId.error };
      return {
        ok: true,
        msg: playerId === undefined ? { type, delta: o.delta } : { type, delta: o.delta, playerId },
      };
    }
    case 'admin_god_teleport': {
      if (typeof o.nodeId !== 'string') return { ok: false, error: 'nodeId required' };
      const playerId = parseOptionalPlayerId(o.playerId);
      if (typeof playerId === 'object') return { ok: false, error: playerId.error };
      return {
        ok: true,
        msg:
          playerId === undefined
            ? { type, nodeId: o.nodeId }
            : { type, nodeId: o.nodeId, playerId },
      };
    }
    case 'admin_god_use_item': {
      if (typeof o.itemId !== 'string') return { ok: false, error: 'itemId required' };
      const playerId = parseOptionalPlayerId(o.playerId);
      if (typeof playerId === 'object') return { ok: false, error: playerId.error };
      const parsedTarget = parseGodModeTarget(o.target);
      if (!parsedTarget.ok) return parsedTarget;
      const base =
        playerId === undefined ? { type, itemId: o.itemId } : { type, itemId: o.itemId, playerId };
      return {
        ok: true,
        msg: parsedTarget.target === undefined ? base : { ...base, target: parsedTarget.target },
      };
    }
    case 'admin_god_set_resources': {
      const playerId = parseOptionalPlayerId(o.playerId);
      if (typeof playerId === 'object') return { ok: false, error: playerId.error };
      if (o.hp !== undefined && !isFiniteNumber(o.hp)) return { ok: false, error: 'hp invalid' };
      if (o.keys !== undefined && !isFiniteNumber(o.keys)) {
        return { ok: false, error: 'keys invalid' };
      }
      if (o.gold !== undefined && !isFiniteNumber(o.gold)) {
        return { ok: false, error: 'gold invalid' };
      }
      if (
        o.inventory !== undefined &&
        (!Array.isArray(o.inventory) || !o.inventory.every((item) => typeof item === 'string'))
      ) {
        return { ok: false, error: 'inventory invalid' };
      }
      const msg: Extract<ClientMessage, { type: 'admin_god_set_resources' }> = { type };
      if (playerId !== undefined) msg.playerId = playerId;
      if (typeof o.hp === 'number') msg.hp = o.hp;
      if (typeof o.keys === 'number') msg.keys = o.keys;
      if (typeof o.gold === 'number') msg.gold = o.gold;
      if (Array.isArray(o.inventory)) msg.inventory = [...o.inventory] as string[];
      return { ok: true, msg };
    }
    case 'admin_god_add_item':
    case 'god_add_item': {
      if (typeof o.itemId !== 'string') return { ok: false, error: 'itemId required' };
      const playerId = parseOptionalPlayerId(o.playerId);
      if (typeof playerId === 'object') return { ok: false, error: playerId.error };
      const normalizedType = 'admin_god_add_item';
      return {
        ok: true,
        msg:
          playerId === undefined
            ? { type: normalizedType, itemId: o.itemId }
            : { type: normalizedType, itemId: o.itemId, playerId },
      };
    }
    case 'admin_god_set_immunity':
    case 'god_set_immunity': {
      const playerId = parseOptionalPlayerId(o.playerId);
      if (typeof playerId === 'object') return { ok: false, error: playerId.error };
      if (typeof o.noDamage !== 'boolean') return { ok: false, error: 'noDamage required' };
      if (typeof o.noKeyLoss !== 'boolean') return { ok: false, error: 'noKeyLoss required' };
      const normalizedType = 'admin_god_set_immunity';
      return {
        ok: true,
        msg:
          playerId === undefined
            ? { type: normalizedType, noDamage: o.noDamage, noKeyLoss: o.noKeyLoss }
            : { type: normalizedType, playerId, noDamage: o.noDamage, noKeyLoss: o.noKeyLoss },
      };
    }
    case 'admin_god_advance_turn':
      return { ok: true, msg: { type } };
    case 'admin_god_force_phase': {
      if (typeof o.phase !== 'string' || !GAME_PHASES.has(o.phase as GameState['phase'])) {
        return { ok: false, error: 'phase required' };
      }
      return { ok: true, msg: { type, phase: o.phase as GameState['phase'] } };
    }
    case 'admin_god_pick_fork': {
      if (typeof o.nodeId !== 'string') return { ok: false, error: 'nodeId required' };
      return { ok: true, msg: { type, nodeId: o.nodeId } };
    }
    case 'emote': {
      if (typeof o.playerId !== 'string') return { ok: false, error: 'playerId required' };
      if (
        typeof o.emoteId !== 'number' ||
        !Number.isInteger(o.emoteId) ||
        o.emoteId < 1 ||
        o.emoteId > 6
      ) {
        return { ok: false, error: 'emoteId must be integer 1-6' };
      }
      return {
        ok: true,
        msg: { type, playerId: o.playerId, emoteId: o.emoteId as 1 | 2 | 3 | 4 | 5 | 6 },
      };
    }
    case 'set_character_customization': {
      const parsed = parseCharacterCustomization(o.customization);
      if (!parsed.ok) return parsed;
      return {
        ok: true,
        msg: {
          type,
          customization: parsed.customization,
          ...(typeof o.playerId === 'string' ? { playerId: o.playerId } : {}),
        },
      };
    }
    default:
      return { ok: false, error: 'unknown type' };
  }
}
